/**
 * P41 · point 6 — "Who represents me" (`Quién me representa`): the half that was missing for the STUDENT.
 *
 * The `my_ward_academics` stream hands guardians the enrollments,
 * grades and attendance of their wards **without the student
 * knowing**. This screen doesn't change what the guardian sees: it tells the
 * student who represents them, what that person gets to see, how their
 * age was resolved and what the current threshold is at their academy.
 *
 * It isn't gated by feature, just like the endpoint that feeds it: knowing
 * who represents you and what they see of you isn't a feature an academy
 * can turn off for its student body.
 *
 * ── The threshold is READ, never written ───────────────────────────────────
 *
 * `consentAgeYears` comes in the response, resolved by `app_config` for that
 * specific academy. There's not a single age number here: an academy declaring
 * 14 has to see 14 without anyone touching code or redeploying. It's the reason
 * this whole campaign exists, and that's why it has its own test
 * (`my-guardianship-view.test.tsx`, layer A).
 *
 * ── The age that's NOT on record is the MAJORITY case ───────────────────────
 *
 * Of the seven entry paths into the system only one records the date of
 * birth, and it's optional; the importer through which most of the
 * student body enters doesn't even have a column for it. That's why `absent` isn't painted as a
 * gray detail: it's painted as a warning, with the consequence (counts as a minor) and with the
 * way out (ask the academy for it) — because **the student can't fix it
 * themselves**: the write-path rejects `student_profile`, the academy fills in
 * their profile.
 *
 * ── Why the six denial reasons aren't shown ──────────────────────────────────
 *
 * `ConsentDenial` has six variants (`unknown_age`, `unreadable_age`,
 * `no_guardian`, `consent_pending`, `consent_revoked`, `scope_not_covered`),
 * but **none of them reach the client**: the write-path silently discards with
 * 2xx (a 4xx from `uploadData` would freeze the queue forever). Showing them
 * would require the client to RE-DERIVE the decision from the age, the
 * threshold and the link's state — a second copy of `consent::policy`
 * living in TypeScript, which is exactly what the "each piece of logic
 * lives in one place" rule forbids. What IS shown are the FACTS the server already
 * resolved, which is where those six reasons come from.
 */
import { msg } from "@lingui/core/macro";
import { type Component, For, Show } from "solid-js";
import { AlertTriangle, ShieldCheck, Users } from "@fleet/icons";
import { academyPeopleQueryKeys, type MyGuardianship } from "@space/shared";
import { api, createApiQuery } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { Badge, PageContainer, PageHeader, Skeleton } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { useActiveOrg } from "../../hooks/use-active-org";
import {
  AGE_RESOLUTION_LABELS,
  CONSENT_SCOPE_LABELS,
  CONSENT_STATUS_LABELS,
  GUARDIAN_SEES_LABELS,
  labelFor,
} from "./labels";

/** An age that's not on record, or that can't be read, counts as a minor. */
const AGE_NEEDS_ATTENTION = new Set(["absent", "unreadable"]);

export const MyGuardianshipView: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();

  const guardianship = createApiQuery<MyGuardianship, unknown>(
    () => academyPeopleQueryKeys.myGuardianship(activeOrgId()),
    async () =>
      api.GET("/api/me/guardianship", { params: { query: { org: activeOrgId() ?? "" } } }),
    () => ({ enabled: Boolean(activeOrgId()) }),
  );

  const view = () => guardianship.data;
  const guardians = () => view()?.guardians ?? [];

  return (
    <PageContainer maxWidth="3xl">
      <div class="space-y-6">
        <PageHeader
          title={t(msg`Who represents me`)}
          description={t(
            msg`Your guardians in this academy, what they can see about you, and what your taking part depends on.`,
          )}
          icon={Users}
          backTo="/profile"
        />

        <Show when={!guardianship.isLoading} fallback={<Skeleton class="h-64 w-full rounded-xl" />}>
          <Show when={view()}>
            {(data) => (
              <div class="space-y-6">
                {/* ── Edad y umbral ───────────────────────────────────────── */}
                <section
                  class="space-y-2 rounded-xl border border-border p-4"
                  data-testid="guardianship-age"
                >
                  <h2 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(msg`Your age on file`)}
                  </h2>
                  <Show when={labelFor(AGE_RESOLUTION_LABELS, data().ageResolution)}>
                    {(label) => (
                      <p
                        class="flex items-start gap-2 text-sm"
                        classList={{
                          "text-warning": AGE_NEEDS_ATTENTION.has(data().ageResolution),
                        }}
                      >
                        <Show when={AGE_NEEDS_ATTENTION.has(data().ageResolution)}>
                          <AlertTriangle class="mt-0.5 h-4 w-4 shrink-0" />
                        </Show>
                        <span>{t(label())}</span>
                      </p>
                    )}
                  </Show>
                  <Show when={AGE_NEEDS_ATTENTION.has(data().ageResolution)}>
                    <p class="text-xs text-muted-foreground">
                      {t(
                        msg`You cannot fix this yourself: your record is filled in by the academy. Ask them to complete it.`,
                      )}
                    </p>
                  </Show>
                  <p class="text-xs text-muted-foreground" data-testid="guardianship-threshold">
                    {(() => {
                      const years = data().consentAgeYears;
                      return t(
                        msg`In this academy, students under ${years} need their guardian's authorization to take part.`,
                      );
                    })()}
                  </p>
                </section>

                {/* ── Mis representantes ──────────────────────────────────── */}
                <section class="space-y-3" data-testid="guardianship-guardians">
                  <h2 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(msg`Your guardians`)}
                  </h2>
                  <Show
                    when={guardians().length > 0}
                    fallback={
                      <EmptyState
                        icon={Users}
                        title={t(msg`Nobody represents you in this academy`)}
                        description={t(
                          msg`If that is not right, tell the academy: only they can create the link.`,
                        )}
                      />
                    }
                  >
                    <ul class="space-y-2">
                      <For each={guardians()}>
                        {(g) => (
                          <li
                            class="space-y-2 rounded-xl border border-border p-3"
                            data-testid="guardianship-row"
                          >
                            <div class="flex flex-wrap items-center justify-between gap-2">
                              <div>
                                <p class="text-sm font-semibold">
                                  {g.guardianName ?? t(msg`Guardian`)}
                                </p>
                                <Show when={g.relationship}>
                                  {(rel) => <p class="text-xs text-muted-foreground">{rel()}</p>}
                                </Show>
                              </div>
                              <Show when={labelFor(CONSENT_STATUS_LABELS, g.consentStatus)}>
                                {(label) => (
                                  <Badge
                                    variant={
                                      g.consentStatus === "granted" ? "outline" : "secondary"
                                    }
                                  >
                                    {t(label())}
                                  </Badge>
                                )}
                              </Show>
                            </div>
                            <Show when={g.scopes.length > 0}>
                              <p class="text-xs text-muted-foreground">
                                {t(msg`Authorized for:`)}{" "}
                                <For each={g.scopes}>
                                  {(scope, i) => (
                                    <Show when={labelFor(CONSENT_SCOPE_LABELS, scope)}>
                                      {(label) => (
                                        <span>
                                          <Show when={i() > 0}>{" · "}</Show>
                                          {t(label())}
                                        </span>
                                      )}
                                    </Show>
                                  )}
                                </For>
                              </p>
                            </Show>
                          </li>
                        )}
                      </For>
                    </ul>
                    <p class="text-xs text-muted-foreground">
                      {t(
                        msg`A link is not a permission: while your guardian has not authorized it, it counts as pending.`,
                      )}
                    </p>
                  </Show>
                </section>

                {/* ── What a guardian sees ─────────────────────────────────────────────── */}
                <section class="space-y-3" data-testid="guardianship-sees">
                  <h2 class="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    <ShieldCheck class="h-4 w-4" />
                    {t(msg`What a linked guardian can see`)}
                  </h2>
                  <ul class="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                    <For each={data().sees}>
                      {(key) => (
                        <Show when={labelFor(GUARDIAN_SEES_LABELS, key)}>
                          {(label) => <li>{t(label())}</li>}
                        </Show>
                      )}
                    </For>
                  </ul>
                  <p class="text-xs text-muted-foreground">
                    {t(
                      msg`They see this because they are linked to you, whether or not they have authorized your participation.`,
                    )}
                  </p>
                </section>
              </div>
            )}
          </Show>
        </Show>
      </div>
    </PageContainer>
  );
};
